import { loginResponseSchema, signInHandoffDTOSchema, signInHandoffExchangeDTOSchema, meDTOSchema, RELEASE_FLAGS_PATH, resolvedReleaseFlagsDTOSchema, signupResponseSchema, ssoReauthRequiredDetailsSchema, termsAcceptanceDTOSchema, verifyResponseSchema, } from '@instanexus/contracts';
import { z } from 'zod';
import { ApiError } from '../api-error.js';
import { dataEnvelopeSchema, WEB_API_PREFIX } from '../transport.js';
const loginEnvelopeSchema = dataEnvelopeSchema(loginResponseSchema);
const handoffEnvelopeSchema = dataEnvelopeSchema(signInHandoffDTOSchema);
const handoffExchangeEnvelopeSchema = dataEnvelopeSchema(signInHandoffExchangeDTOSchema);
const meEnvelopeSchema = dataEnvelopeSchema(meDTOSchema);
const releaseFlagsEnvelopeSchema = dataEnvelopeSchema(resolvedReleaseFlagsDTOSchema);
/**
 * The no-body `204` schema. `parseJson` in `../transport.js` resolves an empty response
 * body to `undefined`, so `z.undefined()` is what "there is genuinely no payload" looks
 * like as a schema — identical to `devices.ts#revoke` and `departments.ts#remove`.
 */
const noContentSchema = z.undefined();
const termsEnvelopeSchema = dataEnvelopeSchema(termsAcceptanceDTOSchema);
const signupEnvelopeSchema = dataEnvelopeSchema(signupResponseSchema);
const verifyEnvelopeSchema = dataEnvelopeSchema(verifyResponseSchema);
export function createAuthResource(transport) {
    /** The refresh currently in flight, shared by every concurrent caller — see `refreshSession`. */
    let refreshInFlight;
    async function requestRefresh() {
        try {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/session`,
                schema: loginEnvelopeSchema,
                method: 'POST',
                // No access token exists yet — obtaining one is the point. Asking for the user auth
                // header here would send a stale or absent credential and could log the caller out
                // through the SDK's own 401 observer before the refresh ever ran.
                auth: 'none',
            });
            return { kind: 'session', response: envelope.data };
        }
        catch (e) {
            // 401 is the NORMAL "no live session" answer, not a fault: no cookie, an expired one,
            // or a membership that has since been revoked. Anything else is a real failure and is
            // rethrown, so an outage cannot masquerade as a signed-out user.
            if (e instanceof ApiError && e.statusCode === 401) {
                if (e.code === 'SSO_REAUTH_REQUIRED') {
                    const details = ssoReauthRequiredDetailsSchema.safeParse(e.details);
                    if (details.success)
                        return { kind: 'reauth', ...details.data };
                }
                return { kind: 'none' };
            }
            throw e;
        }
    }
    function sharedRefresh() {
        if (refreshInFlight === undefined) {
            // Cleared on settle, success or failure alike: dedupe covers only calls that overlap
            // one request, never caches its answer.
            refreshInFlight = requestRefresh().finally(() => {
                refreshInFlight = undefined;
            });
        }
        return refreshInFlight;
    }
    return {
        async login(input) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/login`,
                schema: loginEnvelopeSchema,
                method: 'POST',
                body: input,
            });
            return envelope.data;
        },
        async issueHandoff(input) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/handoff`,
                schema: handoffEnvelopeSchema,
                method: 'POST',
                body: input,
                // The central session cookie is the credential; no bearer token exists, so the SDK's
                // 401 observer must not treat SIGN_IN_REQUIRED as an expired token.
                auth: 'none',
            });
            return envelope.data;
        },
        async exchangeHandoff(input) {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/handoff/exchange`,
                schema: handoffExchangeEnvelopeSchema,
                method: 'POST',
                body: input,
                auth: 'none',
            });
            return envelope.data;
        },
        async refreshSession() {
            const outcome = await sharedRefresh();
            return outcome.kind === 'session' ? outcome.response : undefined;
        },
        resumeSession() {
            return sharedRefresh();
        },
        async logout() {
            await transport.request({
                path: `${WEB_API_PREFIX}/auth/logout`,
                schema: noContentSchema,
                method: 'POST',
                // The cookie is the credential here, exactly as for `refreshSession`. A user whose
                // access token has already expired must still be able to sign out.
                auth: 'none',
            });
        },
        async me() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/me`,
                schema: meEnvelopeSchema,
            });
            return envelope.data;
        },
        async releaseFlags() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}${RELEASE_FLAGS_PATH}`,
                schema: releaseFlagsEnvelopeSchema,
            });
            return envelope.data;
        },
        async acceptTerms() {
            const envelope = await transport.request({
                path: `${WEB_API_PREFIX}/auth/terms-acceptance`,
                schema: termsEnvelopeSchema,
                method: 'POST',
                body: { acceptTerms: true },
            });
            return envelope.data;
        },
        async forgotPassword(input) {
            await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/forgot-password`,
                schema: noContentSchema,
                body: input,
                auth: 'none',
            });
        },
        async resetPassword(input) {
            await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/reset-password`,
                schema: noContentSchema,
                body: input,
                auth: 'none',
            });
        },
        async changePassword(input) {
            await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/change-password`,
                schema: noContentSchema,
                body: input,
            });
        },
        async acceptMemberInvite(input) {
            await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/accept-invite`,
                schema: noContentSchema,
                body: input,
                auth: 'none',
            });
        },
        async signup(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/signup`,
                schema: signupEnvelopeSchema,
                body: input,
                // Public: a stale bearer token from a previous session must not ride along.
                auth: 'none',
            });
            return envelope.data;
        },
        async verifyEmail(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/verify-email`,
                schema: verifyEnvelopeSchema,
                body: input,
                auth: 'none',
            });
            return envelope.data;
        },
        async verifyPhone(input) {
            const envelope = await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/verify-phone`,
                schema: verifyEnvelopeSchema,
                body: input,
                auth: 'none',
            });
            return envelope.data;
        },
        async resendCode(input) {
            await transport.request({
                method: 'POST',
                path: `${WEB_API_PREFIX}/auth/resend-code`,
                // The same no-body `204` shape as `forgotPassword` above — one shape, not a second.
                schema: noContentSchema,
                body: input,
                // Public: a stale bearer token from a previous session must not ride along.
                auth: 'none',
            });
        },
    };
}
//# sourceMappingURL=auth.js.map